import dcmjs from 'dcmjs';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';

const PIXEL_DATA_TAG = '7FE00010';
const BINARY_VRS = new Set(['OB', 'OW', 'OF', 'OD', 'UN']);

export interface DicomTag {
    id: string;
    tag: string;
    name: string;
    vr: string;
    value: string;
    depth: number;
    children?: DicomTag[];
}

function formatValue(vr: string, element: { Value?: unknown[] }): string {
    if (!element.Value || element.Value.length === 0) {
        return '';
    }

    if (BINARY_VRS.has(vr)) {
        return '[Binary data]';
    }

    try {
        const parts = element.Value.map((v: unknown) =>
            typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')
        );

        if ((vr === 'DS' || vr === 'IS') && parts.length > 1) {
            return `[${parts.join(', ')}]`;
        }

        return parts.join(' \\ ');
    } catch {
        return '[Unable to display]';
    }
}

function extractTagsRecursive(
    denaturalized: Record<string, { vr: string; Value?: unknown[] }>,
    depth: number,
    idPrefix: string
): DicomTag[] {
    const tags: DicomTag[] = [];

    for (const rawTag in denaturalized) {
        if (rawTag === PIXEL_DATA_TAG) {
            continue;
        }

        const punctuated = dcmjs.data.DicomMetaDictionary.punctuateTag(rawTag);
        const entry = dcmjs.data.DicomMetaDictionary.dictionary[punctuated] as
            | { name: string; vr: string }
            | undefined;
        const element = denaturalized[rawTag] as {
            vr: string;
            Value?: unknown[];
        };
        const vr = element.vr ?? entry?.vr ?? '';
        const id = `${idPrefix}${rawTag}`;

        if (vr === 'SQ' && element.Value && element.Value.length > 0) {
            const itemCount = element.Value.length;
            const children: DicomTag[] = [];

            const multiItem = element.Value.length > 1;

            element.Value.forEach((item, index) => {
                if (multiItem) {
                    children.push({
                        id: `${id}._item${index}`,
                        tag: '',
                        name: `Item ${index + 1}`,
                        vr: '',
                        value: '',
                        depth: depth + 1,
                    });
                }

                const childDepth = multiItem ? depth + 2 : depth + 1;
                const itemTags = extractTagsRecursive(
                    item as Record<string, { vr: string; Value?: unknown[] }>,
                    childDepth,
                    `${id}.${index}.`
                );
                children.push(...itemTags);
            });

            tags.push({
                id,
                tag: punctuated,
                name: entry?.name ?? 'Unknown',
                vr,
                value: `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`,
                depth,
                children,
            });
        } else {
            tags.push({
                id,
                tag: punctuated,
                name: entry?.name ?? 'Unknown',
                vr,
                value: formatValue(vr, element),
                depth,
            });
        }
    }

    tags.sort((a, b) => a.tag.localeCompare(b.tag));
    return tags;
}

/**
 * Extract all DICOM tags from a dcmjs dataset wrapper as a tree.
 * Sequences are recursively expanded with child items.
 * Skips PixelData to avoid huge binary blobs.
 */
export function extractDicomTags(wrapper: DcmJsDatasetWrapper): DicomTag[] {
    if (!wrapper.dataset) {
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const denaturalized = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(
        wrapper.dataset as Record<string, unknown>
    );

    return extractTagsRecursive(denaturalized, 0, '');
}

/** Flatten a tag tree into a single list (for search mode). */
export function flattenAllTags(tags: DicomTag[]): DicomTag[] {
    const result: DicomTag[] = [];
    for (const tag of tags) {
        result.push(tag);
        if (tag.children) {
            result.push(...flattenAllTags(tag.children));
        }
    }
    return result;
}

/** Flatten only expanded nodes for tree display. */
export function flattenVisibleTags(
    tags: DicomTag[],
    expanded: ReadonlySet<string>
): DicomTag[] {
    const result: DicomTag[] = [];
    for (const tag of tags) {
        result.push(tag);
        if (tag.children && expanded.has(tag.id)) {
            result.push(...flattenVisibleTags(tag.children, expanded));
        }
    }
    return result;
}
